import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';

const COP = n => new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',maximumFractionDigits:0}).format(Number(n)||0);
const hoy = format(new Date(),'yyyy-MM-dd');

const UNIDADES = ['und','kg','g','lb','oz','l','ml','taza','cda','cdta','porciones','láminas','hojas'];
const CATEGORIAS_MP = ['Lácteos','Panadería','Cafetería','Helados','Bebidas','Empaque','Frutas','Otros'];

// ── MODAL RECETA ──────────────────────────────────────────────
function ModalReceta({ producto, materiasPrimas, onGuardar, onCerrar }) {
  const [receta, setReceta] = useState({});

  useEffect(() => {
    const r = {};
    producto.receta?.forEach(x => { r[x.mp_id] = x.cantidad; });
    setReceta(r);
  }, [producto]);

  const porCategoria = {};
  materiasPrimas.forEach(mp => {
    if (!porCategoria[mp.categoria]) porCategoria[mp.categoria] = [];
    porCategoria[mp.categoria].push(mp);
  });

  const handleGuardar = () => {
    const payload = Object.entries(receta)
      .filter(([,v]) => parseFloat(v) > 0)
      .map(([mp_id, cantidad]) => ({ item_id: parseInt(mp_id), cantidad: parseFloat(cantidad) }));
    onGuardar(producto.id, payload);
  };

  const totalMPs = Object.values(receta).filter(v => parseFloat(v) > 0).length;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'white', borderRadius:16, width:'100%', maxWidth:620, maxHeight:'88vh', overflow:'hidden', display:'flex', flexDirection:'column' }}>
        <div style={{ background:'linear-gradient(135deg,#1a1a2e,#16213e)', padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ color:'white', fontWeight:800, fontSize:16 }}>📋 Receta: {producto.nombre}</div>
            <div style={{ color:'rgba(255,255,255,0.5)', fontSize:12, marginTop:2 }}>
              {totalMPs > 0 ? `${totalMPs} materia${totalMPs>1?'s':''} prima definida${totalMPs>1?'s':''}` : 'Define las materias primas que consume este producto'}
            </div>
          </div>
          <button onClick={onCerrar} style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'white', borderRadius:8, padding:'6px 12px', cursor:'pointer', fontSize:18 }}>✕</button>
        </div>
        <div style={{ overflowY:'auto', padding:20, flex:1 }}>
          {materiasPrimas.length === 0 ? (
            <div style={{ textAlign:'center', color:'#9CA3AF', padding:32 }}>Primero agrega materias primas en la pestaña correspondiente</div>
          ) : Object.entries(porCategoria).map(([cat, mps]) => (
            <div key={cat} style={{ marginBottom:20 }}>
              <div style={{ fontWeight:800, fontSize:12, textTransform:'uppercase', color:'#6B7280', marginBottom:10, letterSpacing:'0.05em' }}>{cat}</div>
              {mps.map(mp => (
                <div key={mp.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 0', borderBottom:'1px solid #F9FAFB' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600 }}>{mp.nombre}</div>
                    <div style={{ fontSize:11, color:'#9CA3AF' }}>{mp.unidad}</div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <button onClick={() => setReceta(p => ({...p, [mp.id]: Math.max(0,(parseFloat(p[mp.id])||0)-0.5)}))}
                      style={{ width:26, height:26, border:'2px solid #E5E7EB', borderRadius:6, background:'white', cursor:'pointer', fontWeight:700 }}>−</button>
                    <input type="number" min="0" step="0.01" value={receta[mp.id] ?? ''}
                      onChange={e => setReceta(p => ({...p, [mp.id]: e.target.value}))}
                      placeholder="0"
                      style={{ width:60, padding:'5px', border:`2px solid ${(receta[mp.id]||0)>0?'#6366F1':'#E5E7EB'}`, borderRadius:8, fontSize:13, textAlign:'center', outline:'none' }} />
                    <button onClick={() => setReceta(p => ({...p, [mp.id]: (parseFloat(p[mp.id])||0)+0.5}))}
                      style={{ width:26, height:26, border:'2px solid #E5E7EB', borderRadius:6, background:'white', cursor:'pointer', fontWeight:700 }}>+</button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div style={{ padding:'14px 20px', borderTop:'1px solid #F3F4F6', display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button onClick={onCerrar} style={{ padding:'9px 20px', border:'2px solid #E5E7EB', borderRadius:8, background:'white', cursor:'pointer', fontSize:13, fontWeight:700 }}>Cancelar</button>
          <button onClick={handleGuardar} style={{ padding:'9px 24px', border:'none', borderRadius:8, background:'linear-gradient(135deg,#1a1a2e,#16213e)', color:'white', cursor:'pointer', fontSize:13, fontWeight:700 }}>💾 Guardar receta</button>
        </div>
      </div>
    </div>
  );
}

// ── COMPONENTE PRINCIPAL ──────────────────────────────────────
export default function POSIntegracion() {
  const { usuario } = useAuth();
  const esAdmin = ['gerencia','administracion'].includes(usuario?.rol);
  const esPDV   = usuario?.rol === 'punto_venta';

  const [bodegas, setBodegas]       = useState([]);
  const [bodegaId, setBodegaId]     = useState(null);

  useEffect(() => {
    API.get('/bodegas').then(r => {
      const pvs = r.data.filter(b => b.tipo === 'punto_venta');
      setBodegas(pvs);
      if (esPDV && usuario?.area_id) {
        const mia = pvs.find(b => b.area_id === usuario.area_id);
        if (mia) setBodegaId(mia.id);
      } else if (esAdmin && pvs.length) {
        setBodegaId(pvs[0].id);
      }
    }).catch(() => {});
  }, []);

  const [tab, setTab]                   = useState('ventas');
  const [productos, setProductos]       = useState([]);
  const [materiasPrimas, setMateriasPrimas] = useState([]);
  const [ventas, setVentas]             = useState([]);
  const [cuadre, setCuadre]             = useState(null);
  const [productoModal, setProductoModal] = useState(null);
  const [cargando, setCargando]         = useState(false);
  const [fechaVentas, setFechaVentas]   = useState({ inicio: format(startOfMonth(new Date()),'yyyy-MM-dd'), fin: hoy });
  const [fechaCuadre, setFechaCuadre]   = useState(hoy);

  // Nueva MP
  const [nuevaMP, setNuevaMP]   = useState({ nombre:'', unidad:'und', categoria:'Otros' });
  const [editandoMP, setEditandoMP] = useState(null);
  const [guardandoMP, setGuardandoMP] = useState(false);

  // Gestión de productos
  const [modalProducto, setModalProducto] = useState(null); // null | 'nuevo' | producto
  const [guardandoProd, setGuardandoProd] = useState(false);
  const [formProd, setFormProd] = useState({ nombre:'', precio:'', categoria:'' });

  const cargarProductos = () => {
    if (!bodegaId) return;
    API.get('/pos/productos', { params: { bodega_id: bodegaId } }).then(r => setProductos(r.data)).catch(()=>{});
  };

  const abrirModalProducto = (prod = null) => {
    if (prod) {
      setFormProd({ nombre: prod.nombre, precio: prod.precio, categoria: prod.categoria || '' });
      setModalProducto(prod);
    } else {
      setFormProd({ nombre: '', precio: '', categoria: '' });
      setModalProducto('nuevo');
    }
  };

  const handleGuardarProducto = async () => {
    if (!formProd.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    setGuardandoProd(true);
    try {
      if (modalProducto === 'nuevo') {
        await API.post('/pos/productos', { ...formProd, bodega_id: bodegaId });
        toast.success('Producto creado');
      } else {
        await API.put(`/pos/productos/${modalProducto.id}`, formProd);
        toast.success('Producto actualizado');
      }
      setModalProducto(null);
      cargarProductos();
    } catch(e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setGuardandoProd(false); }
  };

  const handleEliminarProducto = async (prod) => {
    if (!window.confirm(`¿Eliminar "${prod.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await API.delete(`/pos/productos/${prod.id}`);
      toast.success('Producto eliminado');
      cargarProductos();
    } catch(e) { toast.error(e.response?.data?.error || 'Error al eliminar'); }
  };

  const cargarMPs = () => {
    if (!bodegaId) return;
    API.get('/pos/materias-primas', { params: { bodega_id: bodegaId } }).then(r => setMateriasPrimas(r.data)).catch(()=>{});
  };

  useEffect(() => {
    if (bodegaId) { cargarProductos(); cargarMPs(); }
  }, [bodegaId]);

  useEffect(() => {
    if (tab === 'ventas' && bodegaId) cargarVentas();
  }, [tab, fechaVentas, bodegaId]);

  const cargarVentas = () => {
    setCargando(true);
    API.get('/pos/ventas', { params: { bodega_id: bodegaId, fecha_inicio: fechaVentas.inicio, fecha_fin: fechaVentas.fin } })
      .then(r => setVentas(r.data)).catch(()=>{}).finally(() => setCargando(false));
  };

  const cargarCuadre = (fecha) => {
    setCargando(true);
    API.get('/pos/cuadre', { params: { bodega_id: bodegaId, fecha: fecha || fechaCuadre } })
      .then(r => setCuadre(r.data)).catch(() => toast.error('Error cargando salidas')).finally(() => setCargando(false));
  };

  useEffect(() => {
    if (tab === 'cuadre' && bodegaId) cargarCuadre(fechaCuadre);
  }, [tab, fechaCuadre, bodegaId]);

  const handleGuardarMP = async () => {
    if (!nuevaMP.nombre.trim()) { toast.error('Ingresa el nombre'); return; }
    setGuardandoMP(true);
    try {
      if (editandoMP) {
        await API.put(`/pos/materias-primas/${editandoMP}`, { ...nuevaMP, bodega_id: bodegaId });
        toast.success('Materia prima actualizada');
        setEditandoMP(null);
      } else {
        await API.post('/pos/materias-primas', { ...nuevaMP, bodega_id: bodegaId });
        toast.success('✅ Materia prima agregada');
      }
      setNuevaMP({ nombre:'', unidad:'und', categoria:'Otros' });
      cargarMPs();
    } catch { toast.error('Error guardando'); }
    finally { setGuardandoMP(false); }
  };

  const handleEliminarMP = async (id) => {
    if (!window.confirm('¿Eliminar esta materia prima?')) return;
    try {
      await API.delete(`/pos/materias-primas/${id}`);
      toast.success('Eliminada');
      cargarMPs();
    } catch { toast.error('Error'); }
  };

  const handleEditarMP = (mp) => {
    setEditandoMP(mp.id);
    setNuevaMP({ nombre: mp.nombre, unidad: mp.unidad, categoria: mp.categoria });
  };

  const handleGuardarReceta = async (producto_id, mps) => {
    try {
      await API.post('/pos/recetas', { producto_id, items: mps });
      toast.success('✅ Receta guardada');
      setProductoModal(null);
      cargarProductos();
    } catch { toast.error('Error guardando receta'); }
  };

  const totalVentas   = ventas.reduce((t,v) => t + parseFloat(v.total||0), 0);
  const totalFacturas = ventas.reduce((t,v) => t + parseInt(v.num_ventas||0), 0);
  const alertas       = cuadre?.cuadre?.filter(c => c.alerta) || [];

  // Agrupar MPs por categoría
  const mpsPorCat = {};
  materiasPrimas.forEach(mp => {
    if (!mpsPorCat[mp.categoria]) mpsPorCat[mp.categoria] = [];
    mpsPorCat[mp.categoria].push(mp);
  });

  return (
    <div style={{ fontFamily:"'Segoe UI',sans-serif", maxWidth:1000, margin:'0 auto' }}>
      <div style={{ marginBottom:20 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
          <div>
            <h1 style={{ margin:0, fontSize:26, fontWeight:800, color:'#111827' }}>🖥️ POS Emergencia</h1>
            <p style={{ margin:'4px 0 0', color:'#6B7280', fontSize:14 }}>Ventas, materias primas, recetas y salidas de inventario</p>
          </div>
          {esAdmin && bodegas.length > 0 && (
            <select value={bodegaId || ''} onChange={e => setBodegaId(Number(e.target.value))}
              style={{ padding:'8px 14px', border:'2px solid #E5E7EB', borderRadius:10, fontSize:14, fontWeight:600, outline:'none', background:'white', cursor:'pointer' }}>
              {bodegas.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, background:'white', borderRadius:12, padding:4, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', marginBottom:20, width:'fit-content', flexWrap:'wrap' }}>
        {[
          { id:'ventas',   label:'📊 Ventas' },
          { id:'mprimas',  label:'🧂 Materias Primas' },
          { id:'recetas',  label:'📋 Recetas' },
          { id:'cuadre', label:'📦 Salidas de inventario' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding:'8px 18px', border:'none', borderRadius:9, cursor:'pointer', fontSize:13,
            fontWeight:tab===t.id?700:500, background:tab===t.id?'#1a1a2e':'transparent', color:tab===t.id?'white':'#6B7280' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── TAB VENTAS ── */}
      {tab === 'ventas' && (
        <>
          <div style={{ display:'flex', gap:10, marginBottom:18, alignItems:'flex-end', flexWrap:'wrap' }}>
            {[{label:'Desde',key:'inicio'},{label:'Hasta',key:'fin'}].map(f => (
              <div key={f.key}>
                <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>{f.label}</div>
                <input type="date" value={fechaVentas[f.key]} onChange={e => setFechaVentas(p => ({...p,[f.key]:e.target.value}))}
                  style={{ padding:'8px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none' }} />
              </div>
            ))}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:12, marginBottom:20 }}>
            {[
              { label:'Total ventas', valor:COP(totalVentas), icon:'💰', color:'#10B981' },
              { label:'Facturas', valor:totalFacturas.toLocaleString(), icon:'🧾', color:'#3B82F6' },
              { label:'Ticket promedio', valor:totalFacturas>0?COP(totalVentas/totalFacturas):'—', icon:'📊', color:'#8B5CF6' },
            ].map((s,i) => (
              <div key={i} style={{ background:'white', borderRadius:12, padding:'14px 16px', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
                <div style={{ fontSize:20, marginBottom:6 }}>{s.icon}</div>
                <div style={{ fontFamily:'monospace', fontWeight:800, fontSize:18, color:s.color }}>{s.valor}</div>
                <div style={{ fontSize:11, color:'#9CA3AF', fontWeight:600, marginTop:2 }}>{s.label}</div>
              </div>
            ))}
          </div>
          <div style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
            <div style={{ padding:'14px 20px', borderBottom:'1px solid #F3F4F6', fontWeight:800, fontSize:15 }}>Ventas por día</div>
            {cargando ? <div style={{ padding:32, textAlign:'center', color:'#9CA3AF' }}>Cargando...</div>
            : ventas.length === 0 ? <div style={{ padding:32, textAlign:'center', color:'#9CA3AF' }}>📭 Sin ventas en este período</div>
            : (
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ background:'#F9FAFB' }}>
                    {['Día','Facturas','Efectivo','Tarjeta','QR','Total'].map(h => (
                      <th key={h} style={{ padding:'10px 14px', textAlign:h==='Día'?'left':'right', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ventas.map((v,i) => (
                    <tr key={v.dia} style={{ borderTop:'1px solid #F3F4F6', background:i%2===0?'white':'#FAFAFA' }}>
                      <td style={{ padding:'10px 14px', fontWeight:700 }}>{(() => { try { return format(new Date(v.dia+'T12:00'),"EEEE d 'de' MMM",{locale:es}); } catch { return v.dia; } })()}</td>
                      <td style={{ padding:'10px 14px', textAlign:'right', color:'#6B7280' }}>{v.num_ventas}</td>
                      <td style={{ padding:'10px 14px', textAlign:'right', fontFamily:'monospace', color:'#10B981', fontWeight:600 }}>{COP(v.efectivo)}</td>
                      <td style={{ padding:'10px 14px', textAlign:'right', fontFamily:'monospace', color:'#3B82F6', fontWeight:600 }}>{COP(v.tarjeta)}</td>
                      <td style={{ padding:'10px 14px', textAlign:'right', fontFamily:'monospace', color:'#F59E0B', fontWeight:600 }}>{COP(v.qr)}</td>
                      <td style={{ padding:'10px 14px', textAlign:'right', fontFamily:'monospace', fontWeight:800 }}>{COP(v.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ── TAB MATERIAS PRIMAS ── */}
      {tab === 'mprimas' && (
        <>
          {/* Formulario nueva MP */}
          {esAdmin && (
            <div style={{ background:'white', borderRadius:14, padding:20, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', marginBottom:18 }}>
              <div style={{ fontWeight:800, fontSize:15, marginBottom:14 }}>
                {editandoMP ? '✏️ Editar materia prima' : '➕ Nueva materia prima'}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 140px 160px auto', gap:10, alignItems:'flex-end', flexWrap:'wrap' }}>
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Nombre</div>
                  <input value={nuevaMP.nombre} onChange={e => setNuevaMP(p => ({...p, nombre:e.target.value}))}
                    placeholder="Ej: Croissant congelado"
                    style={{ width:'100%', padding:'9px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} />
                </div>
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Unidad</div>
                  <select value={nuevaMP.unidad} onChange={e => setNuevaMP(p => ({...p, unidad:e.target.value}))}
                    style={{ width:'100%', padding:'9px 10px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', background:'white' }}>
                    {UNIDADES.map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Categoría</div>
                  <select value={nuevaMP.categoria} onChange={e => setNuevaMP(p => ({...p, categoria:e.target.value}))}
                    style={{ width:'100%', padding:'9px 10px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', background:'white' }}>
                    {CATEGORIAS_MP.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={handleGuardarMP} disabled={guardandoMP}
                    style={{ padding:'9px 18px', border:'none', borderRadius:8, background:'#1a1a2e', color:'white', cursor:'pointer', fontSize:13, fontWeight:700, whiteSpace:'nowrap' }}>
                    {guardandoMP ? '⏳' : editandoMP ? '✓ Guardar' : '+ Agregar'}
                  </button>
                  {editandoMP && (
                    <button onClick={() => { setEditandoMP(null); setNuevaMP({ nombre:'', unidad:'und', categoria:'Otros' }); }}
                      style={{ padding:'9px 14px', border:'2px solid #E5E7EB', borderRadius:8, background:'white', cursor:'pointer', fontSize:13, fontWeight:700 }}>✕</button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Lista por categoría */}
          {materiasPrimas.length === 0 ? (
            <div style={{ background:'white', borderRadius:14, padding:48, textAlign:'center', color:'#9CA3AF', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ fontSize:32, marginBottom:8 }}>🧂</div>
              Agrega las materias primas para luego asignarlas a las recetas
            </div>
          ) : Object.entries(mpsPorCat).map(([cat, mps]) => (
            <div key={cat} style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', marginBottom:14 }}>
              <div style={{ background:'#1a1a2e', padding:'10px 18px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ color:'white', fontWeight:800, fontSize:13, textTransform:'uppercase' }}>{cat}</span>
                <span style={{ color:'rgba(255,255,255,0.5)', fontSize:11 }}>{mps.length} items</span>
              </div>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ background:'#F9FAFB' }}>
                    <th style={{ padding:'8px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#6B7280' }}>NOMBRE</th>
                    <th style={{ padding:'8px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#6B7280' }}>UNIDAD</th>
                    {esAdmin && <th style={{ padding:'8px 16px', textAlign:'right', fontSize:11, fontWeight:700, color:'#6B7280' }}>ACCIONES</th>}
                  </tr>
                </thead>
                <tbody>
                  {mps.map((mp, i) => (
                    <tr key={mp.id} style={{ borderTop:'1px solid #F3F4F6', background:i%2===0?'white':'#FAFAFA' }}>
                      <td style={{ padding:'10px 16px', fontWeight:600 }}>{mp.nombre}</td>
                      <td style={{ padding:'10px 16px', color:'#6B7280' }}>{mp.unidad}</td>
                      {esAdmin && (
                        <td style={{ padding:'10px 16px', textAlign:'right' }}>
                          <button onClick={() => handleEditarMP(mp)} style={{ padding:'4px 10px', border:'1px solid #E5E7EB', borderRadius:6, background:'white', cursor:'pointer', fontSize:12, fontWeight:600, marginRight:6 }}>✏️ Editar</button>
                          <button onClick={() => handleEliminarMP(mp.id)} style={{ padding:'4px 10px', border:'1px solid #FEE2E2', borderRadius:6, background:'#FFF5F5', cursor:'pointer', fontSize:12, fontWeight:600, color:'#DC2626' }}>🗑️</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}

      {/* ── TAB RECETAS ── */}
      {tab === 'recetas' && (
        <div style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ padding:'14px 20px', borderBottom:'1px solid #F3F4F6', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
            <div style={{ fontWeight:800, fontSize:15 }}>📋 Recetas por producto</div>
            <div style={{ display:'flex', gap:16, fontSize:12, color:'#6B7280' }}>
              <span>✅ Con receta: <strong>{productos.filter(p => p.receta?.length>0).length}</strong></span>
              <span>🏪 Terminados: <strong>{productos.filter(p => p.es_terminado).length}</strong></span>
              <span>⚠️ Sin definir: <strong>{productos.filter(p => !p.receta?.length && !p.es_terminado).length}</strong></span>
            </div>
          </div>
          {esAdmin && (
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #F3F4F6', display:'flex', justifyContent:'flex-end' }}>
              <button onClick={() => abrirModalProducto()}
                style={{ padding:'8px 18px', border:'none', borderRadius:8, background:'linear-gradient(135deg,#1a1a2e,#3B4E8C)', color:'white', cursor:'pointer', fontSize:13, fontWeight:700 }}>
                + Nuevo producto
              </button>
            </div>
          )}
          {materiasPrimas.length === 0 && (
            <div style={{ background:'#FEF3C7', padding:'10px 20px', fontSize:13, color:'#92400E', fontWeight:600 }}>
              ⚠️ Primero agrega materias primas en la pestaña "Materias Primas"
            </div>
          )}
          <div style={{ padding:16, display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:12 }}>
            {productos.map(p => {
              const estado = p.es_terminado ? 'terminado' : p.receta?.length>0 ? 'con_receta' : 'sin_definir';
              const borderColor = estado==='terminado' ? '#DBEAFE' : estado==='con_receta' ? '#D1FAE5' : '#F3F4F6';
              const badgeBg    = estado==='terminado' ? '#DBEAFE' : estado==='con_receta' ? '#D1FAE5' : '#FEE2E2';
              const badgeColor = estado==='terminado' ? '#1E40AF' : estado==='con_receta' ? '#065F46' : '#991B1B';
              const badgeLabel = estado==='terminado' ? '🏪 Terminado' : estado==='con_receta' ? `${p.receta.length} MP` : 'Sin definir';
              return (
                <div key={p.id} style={{ border:'2px solid', borderColor, borderRadius:12, padding:'14px 16px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                    <div style={{ flex:1, marginRight:8 }}>
                      <div style={{ fontWeight:700, fontSize:13 }}>{p.nombre}</div>
                      <div style={{ fontSize:12, color:'#9CA3AF' }}>{COP(p.precio)}</div>
                    </div>
                    <span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, fontWeight:700, background:badgeBg, color:badgeColor, whiteSpace:'nowrap' }}>
                      {badgeLabel}
                    </span>
                  </div>

                  {estado === 'con_receta' && (
                    <div style={{ marginBottom:10 }}>
                      {p.receta.map(r => (
                        <div key={r.mp_id} style={{ fontSize:11, color:'#374151', padding:'2px 0', display:'flex', justifyContent:'space-between' }}>
                          <span>• {r.mp_nombre || r.item_nombre}</span>
                          <span style={{ fontWeight:700, color:'#6366F1' }}>{r.cantidad} {r.mp_unidad || r.unidad}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {estado === 'terminado' && (
                    <div style={{ fontSize:11, color:'#1E40AF', marginBottom:10, padding:'6px 10px', background:'#EFF6FF', borderRadius:6 }}>
                      Se vende tal cual, no consume materias primas
                    </div>
                  )}

                  {esAdmin && (
                    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                      <div style={{ display:'flex', gap:6 }}>
                        {estado !== 'terminado' && (
                          <button onClick={() => setProductoModal(p)}
                            style={{ flex:1, padding:'7px', border:'2px solid #E5E7EB', borderRadius:8, background:'white', cursor:'pointer', fontSize:11, fontWeight:700, color:'#374151' }}>
                            ✏️ {estado==='con_receta'?'Editar receta':'Definir receta'}
                          </button>
                        )}
                        <button onClick={async () => {
                            try {
                              await API.put(`/pos/productos/${p.id}/terminado`, { es_terminado: !p.es_terminado });
                              cargarProductos();
                            } catch { toast.error('Error'); }
                          }}
                          style={{ flex:1, padding:'7px', border:`2px solid ${p.es_terminado?'#BFDBFE':'#E5E7EB'}`,
                            borderRadius:8, background:p.es_terminado?'#EFF6FF':'white',
                            cursor:'pointer', fontSize:11, fontWeight:700,
                            color:p.es_terminado?'#1E40AF':'#6B7280' }}>
                          {p.es_terminado ? '↩️ Tiene receta' : '🏪 Terminado'}
                        </button>
                      </div>
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={() => abrirModalProducto(p)}
                          style={{ flex:1, padding:'6px', border:'1px solid #E5E7EB', borderRadius:7, background:'white', cursor:'pointer', fontSize:11, fontWeight:600, color:'#374151' }}>
                          ✏️ Editar
                        </button>
                        <button onClick={() => handleEliminarProducto(p)}
                          style={{ flex:1, padding:'6px', border:'1px solid #FEE2E2', borderRadius:7, background:'#FFF5F5', cursor:'pointer', fontSize:11, fontWeight:600, color:'#DC2626' }}>
                          🗑️ Eliminar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── TAB CUADRE ── */}
      {tab === 'cuadre' && (
        <>
          <div style={{ display:'flex', gap:10, marginBottom:18, alignItems:'flex-end', flexWrap:'wrap' }}>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Fecha</div>
              <input type="date" value={fechaCuadre} onChange={e => setFechaCuadre(e.target.value)} max={hoy}
                style={{ padding:'8px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none' }} />
            </div>
            {cargando && <span style={{ fontSize:13, color:'#6B7280', padding:'8px 0' }}>⏳ Calculando...</span>}
          </div>

          {cuadre && cuadre.cuadre?.length === 0 && (
            <div style={{ background:'white', borderRadius:14, padding:32, textAlign:'center', color:'#9CA3AF', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
              Sin ventas registradas para esta fecha.
            </div>
          )}

          {cuadre && cuadre.cuadre?.length > 0 && (
            <div style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ background:'#1a1a2e', padding:'12px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ color:'white', fontWeight:800, fontSize:14 }}>
                  📦 Salidas del {fechaCuadre ? format(new Date(fechaCuadre+'T12:00'),"d 'de' MMMM yyyy",{locale:es}) : ''} — {bodegas.find(b => b.id === bodegaId)?.nombre || 'PDV'}
                </div>
                <div style={{ color:'rgba(255,255,255,0.6)', fontSize:12 }}>
                  {cuadre.cuadre.length} materias primas · {cuadre.total_ventas || 0} ventas
                </div>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ background:'#F9FAFB' }}>
                      <th style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>Materia prima</th>
                      <th style={{ padding:'10px 14px', textAlign:'right', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>Unidad</th>
                      <th style={{ padding:'10px 14px', textAlign:'right', fontSize:11, fontWeight:700, color:'#EF4444', textTransform:'uppercase' }}>Consumo teórico</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cuadre.cuadre
                      .filter(c => c.consumo_teorico > 0)
                      .sort((a,b) => b.consumo_teorico - a.consumo_teorico)
                      .map((c,i) => (
                      <tr key={c.item_id} style={{ borderTop:'1px solid #F3F4F6', background:i%2===0?'white':'#FAFAFA' }}>
                        <td style={{ padding:'10px 14px', fontWeight:600, color:'#111827' }}>{c.nombre}</td>
                        <td style={{ padding:'10px 14px', textAlign:'right', color:'#6B7280', fontSize:12 }}>{c.unidad || '—'}</td>
                        <td style={{ padding:'10px 14px', textAlign:'right', fontFamily:'monospace', fontWeight:800, color:'#EF4444', fontSize:14 }}>
                          -{c.consumo_teorico.toFixed(3)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal receta */}
      {productoModal && (
        <ModalReceta producto={productoModal} materiasPrimas={materiasPrimas}
          onGuardar={handleGuardarReceta} onCerrar={() => setProductoModal(null)} />
      )}

      {/* Modal nuevo/editar producto */}
      {modalProducto !== null && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
          <div style={{ background:'white', borderRadius:16, width:'100%', maxWidth:420, boxShadow:'0 24px 64px rgba(0,0,0,0.3)', overflow:'hidden' }}>
            <div style={{ background:'#1a1a2e', color:'white', padding:'16px 20px' }}>
              <div style={{ fontWeight:800, fontSize:16 }}>{modalProducto === 'nuevo' ? '➕ Nuevo producto' : '✏️ Editar producto'}</div>
            </div>
            <div style={{ padding:'20px', display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Nombre *</div>
                <input value={formProd.nombre} onChange={e => setFormProd(f => ({ ...f, nombre: e.target.value }))}
                  placeholder="Ej: Gelato de mango"
                  style={{ width:'100%', padding:'9px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:14, outline:'none', boxSizing:'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Precio</div>
                <input type="number" value={formProd.precio} onChange={e => setFormProd(f => ({ ...f, precio: e.target.value }))}
                  placeholder="0"
                  style={{ width:'100%', padding:'9px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:14, outline:'none', boxSizing:'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Categoría</div>
                <input value={formProd.categoria} onChange={e => setFormProd(f => ({ ...f, categoria: e.target.value }))}
                  placeholder="Ej: Gelatos, Bebidas..."
                  style={{ width:'100%', padding:'9px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:14, outline:'none', boxSizing:'border-box' }} />
              </div>
            </div>
            <div style={{ display:'flex', gap:10, padding:'0 20px 20px' }}>
              <button onClick={() => setModalProducto(null)}
                style={{ flex:1, padding:'10px', border:'2px solid #E5E7EB', borderRadius:8, background:'white', cursor:'pointer', fontSize:14, fontWeight:600, color:'#374151' }}>
                Cancelar
              </button>
              <button onClick={handleGuardarProducto} disabled={guardandoProd}
                style={{ flex:2, padding:'10px', border:'none', borderRadius:8, background:'linear-gradient(135deg,#1a1a2e,#3B4E8C)', color:'white', cursor:guardandoProd?'not-allowed':'pointer', fontSize:14, fontWeight:700 }}>
                {guardandoProd ? '⏳ Guardando...' : modalProducto === 'nuevo' ? '✅ Crear producto' : '✅ Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
